import { type AppDispatch, type RootState } from "features/Application/globaltypes/redux";
import { useEffect, useState, forwardRef } from "react";
import { connect, type ConnectedProps } from "react-redux";
import TemplateUsers, { type TemplateUsersRef } from "../../TemplateUsers/TemplateUsers";
import { setSelected as setSelectedUsers } from "features/Accounts/state/slices/editAccountUsersSlice";
import { bindAction, type IObservable } from "interfaces";
import { SortingDirection } from "enums";
import { Columns, columnToParamMap } from "../../TemplateUsers/columnOptions";
import { useObserver } from "hooks/useObserver";
import { fetchAccountUsers } from "features/Accounts/state/thunks/editAccountUsersThunk";
import { deleteUsers } from "features/Accounts/EditAccount/state/thunks/peopleThunk";

export type CreateAccountTemplateUsersProps = {
  accountId: number;
  renderAddUsersButton: (tooltipPosition?: string) => JSX.Element;
  onAddUserModalObserver: IObservable<(onConfirm: () => void) => void>;
  onImportUsersModalObserver: IObservable<(onConfirm: () => void) => void>;
  onDeleteUsersObserver: IObservable<(onConfirm: () => void, userIds: number[]) => void>;
} & PropsFromRedux;

const CreateAccountTemplateUsers = forwardRef<TemplateUsersRef, CreateAccountTemplateUsersProps>((props, ref) => {
  const { accountId, fetchAccountUsers, onImportUsersModalObserver } = props;
  const [showUserUploadModal, setShowUsersFileUploadModal] = useState(false);

  const [subscribe] = useObserver(onImportUsersModalObserver);

  useEffect(() => {
    return subscribe(() => setShowUsersFileUploadModal(true));
  }, [subscribe]);

  const loadAccountUsers = (
    skip: number = 0,
    top: number = 10,
    sortingColumnName: string = Columns.Added,
    sortingDirection: SortingDirection = SortingDirection.Descending,
  ) => {
    const sortBy = columnToParamMap[sortingColumnName.toLowerCase() as Lowercase<Columns>];
    fetchAccountUsers(accountId, top, skip, sortBy, sortingDirection);
  };

  return (
    <>
      <TemplateUsers
        {...props}
        ref={ref}
        editAccountId={accountId}
        fetchUsers={loadAccountUsers}
        onCloseUsersFileUploadModal={() => setShowUsersFileUploadModal(false)}
        showUsersFileUploadModal={showUserUploadModal}
      />
    </>
  );
});

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const { templateUsers } = state.accounts;
  const { isLoading, items, selected, itemsCount } = templateUsers;
  return {
    users: items,
    selectedIds: selected,
    isLoading,
    usersCount: itemsCount,
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    onSelectedUsersChanged: bindAction(setSelectedUsers, dispatch),
    fetchAccountUsers: bindAction(fetchAccountUsers, dispatch),
    deleteUsers: bindAction(deleteUsers, dispatch),
  };
};
const connector = connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true });

type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(CreateAccountTemplateUsers);
export default ConnectedComponent;
